import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import WebApp from '@twa-dev/sdk';
import { useTheme } from './ThemeContext';

interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
}

interface TelegramContextType {
  user: TelegramUser | null;
  isInTelegram: boolean;
  initData: string;
  colorScheme: 'light' | 'dark';
  themeParams: any;
  openLink: (url: string) => void;
  openTelegramLink: (url: string) => void;  
  showMainButton: (text: string, onClick: () => void) => void;
  hideMainButton: () => void;
  showAlert: (message: string) => void;
  showConfirm: (message: string, callback: (confirmed: boolean) => void) => void;
  hapticFeedback: (type: 'impact' | 'notification' | 'selection') => void;
  close: () => void;
}

const TelegramContext = createContext<TelegramContextType | undefined>(undefined);

export const useTelegram = () => {
  const context = useContext(TelegramContext);
  if (context === undefined) {
    throw new Error('useTelegram must be used within a TelegramProvider');
  }
  return context;
};

interface TelegramProviderProps {
  children: ReactNode;
}

export const TelegramProvider: React.FC<TelegramProviderProps> = ({ children }) => {
  const [user, setUser] = useState<TelegramUser | null>(null);
  const [isInTelegram, setIsInTelegram] = useState(false);
  const [initData, setInitData] = useState('');
  const [colorScheme, setColorScheme] = useState<'light' | 'dark'>('light');
  const [themeParams, setThemeParams] = useState({});
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    // 检查是否在Telegram环境中
    if (WebApp.initDataUnsafe?.user) {
      setIsInTelegram(true);
      setUser(WebApp.initDataUnsafe.user as TelegramUser);
      setInitData(WebApp.initData);
      setColorScheme(WebApp.colorScheme);
      setThemeParams(WebApp.themeParams);

      // 初始化Telegram Web App设置
      WebApp.ready();
      WebApp.expand();
      WebApp.enableClosingConfirmation();
      
      // 设置主题颜色
      if (WebApp.colorScheme === 'dark') {
        WebApp.setHeaderColor('#1f2937');
        WebApp.setBackgroundColor('#111827');
      } else {
        WebApp.setHeaderColor('#f9fafb');
        WebApp.setBackgroundColor('#f9fafb');
      }

      // 监听主题变化
      WebApp.onEvent('themeChanged', () => {
        setColorScheme(WebApp.colorScheme);
        setThemeParams(WebApp.themeParams);
      });
    }
  }, []);

  const openLink = (url: string) => {
    WebApp.openLink(url);
  };

  const openTelegramLink = (url: string) => {
    WebApp.openTelegramLink(url);
  };

  const showMainButton = (text: string, onClick: () => void) => {
    WebApp.MainButton.setText(text);
    WebApp.MainButton.onClick(onClick);
    WebApp.MainButton.show();
  };

  const hideMainButton = () => {
    WebApp.MainButton.hide();
  };

  const showAlert = (message: string) => {
    WebApp.showAlert(message);
  };

  const showConfirm = (message: string, callback: (confirmed: boolean) => void) => {
    WebApp.showConfirm(message, callback);
  };

  const hapticFeedback = (type: 'impact' | 'notification' | 'selection') => {
    if (WebApp.HapticFeedback) {
      switch (type) {
        case 'impact':
          WebApp.HapticFeedback.impactOccurred('medium');
          break;
        case 'notification':
          WebApp.HapticFeedback.notificationOccurred('success');
          break;
        case 'selection':
          WebApp.HapticFeedback.selectionChanged();
          break;
      }
    }
  };

  const close = () => {
    WebApp.close();
  };

  const value: TelegramContextType = {
    user,
    isInTelegram,
    initData,
    colorScheme: isInTelegram ? colorScheme : resolvedTheme,
    themeParams,
    openLink,
    openTelegramLink,
    showMainButton,
    hideMainButton,
    showAlert,
    showConfirm,
    hapticFeedback,
    close
  };

  return (
    <TelegramContext.Provider value={value}>
      {children}
    </TelegramContext.Provider>
  );
};